import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CircleIndicatorControlEffectiveness } from 'Components/CircleIndicators/CircleIndicatorControlEffectiveness';
import { Text } from 'Components/Text/Text';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { ICON_NEGATIVE, ICON_POSITIVE } from 'Config/Icons';
import { makeComplianceRequirementHumanReadable } from 'Helpers/ComplianceRequirementFormatter/ComplianceRequirementFormatter';
import { controlTextToString, getHumanReadableControlIdFromControl } from 'Helpers/ControlFormatter/ControlFormatter';
import { ellipt } from 'Helpers/StringUtils';
import { AiOperationalControlAssessmentFinalResult } from 'Models/ArtificialIntelligence';
import { ComplianceRequirementForControl, GenericStatus } from 'Models/ComplianceRequirements';
import { OperationalControl } from 'Models/OperationalControls';

import styles from './ArtificialIntelligenceControlAssessment.module.css';

const MAX_CHARS_FOR_TOOLTIP = 300;

export interface ArtificialIntelligenceControlAssessmentResultsProps {
    /** Used only to show the text of each analyzed requirement in a tooltip. */
    complianceRequirements: Pick<ComplianceRequirementForControl, 'identifier' | 'regulation' | 'text'>[];
    control: OperationalControl;
    finalResult: AiOperationalControlAssessmentFinalResult;
}

/**
 * Renders the results of a successfully generated SummIT Guide™ control assessment: the control analysis followed by the analysis of each compliance requirement.
 * Used for the current AI assessment on the Conduct Assessment page and for historical AI assessments on the Historical Control Assessment page.
 */
export const ArtificialIntelligenceControlAssessmentResults = (props: ArtificialIntelligenceControlAssessmentResultsProps) => {
    return (
        <>
            <div className={styles.titleAndTooltipContainer}>
                <Text variant="Header3">{getHumanReadableControlIdFromControl(props.control)}</Text>
                <div className={styles.tooltipForControl}>
                    <FormFieldTooltip text={ellipt(controlTextToString(props.control.metadata.control_text), MAX_CHARS_FOR_TOOLTIP)} />
                </div>
            </div>
            <Text variant="Header4" color="darkGray" noStyles>
                Suggested effectiveness
            </Text>
            <div className={styles.iconAndStatusContainer}>
                <CircleIndicatorControlEffectiveness effectiveness={props.finalResult.control_analysis.control_effectiveness_rating} />
            </div>
            <Text variant="Header4" color="darkGray" noStyles>
                Summary
            </Text>
            <Text>{props.finalResult.control_analysis.summary}</Text>
            <Text variant="Header4" color="darkGray" noStyles>
                Key observations
            </Text>
            <ul className={styles.list}>
                {props.finalResult.control_analysis.key_observations.map((observation, index) => (
                    <li key={index}>
                        <Text variant="PlainText">{observation}</Text>
                    </li>
                ))}
            </ul>
            {props.finalResult.control_analysis.improvements.length > 0 && (
                <>
                    <Text variant="Header4" color="darkGray" noStyles>
                        Suggestions
                    </Text>
                    <ul className={styles.list}>
                        {props.finalResult.control_analysis.improvements.map((suggestion, index) => (
                            <li key={index}>
                                <Text variant="PlainText">{suggestion}</Text>
                            </li>
                        ))}
                    </ul>
                </>
            )}
            <Text variant="Header3">Compliance Requirements</Text>
            {props.finalResult.requirement_analyses.length === 0 && <Text>-</Text>}
            {props.finalResult.requirement_analyses.length > 0 && (
                <>
                    {props.finalResult.requirement_analyses.map((analysis, index) => {
                        const requirement = props.complianceRequirements.find((requirement) => requirement.regulation === analysis.regulation && requirement.identifier === analysis.identifier);

                        return (
                            <div key={index} className={index === 0 ? undefined : styles.marginTop}>
                                <div className={styles.titleAndTooltipContainer}>
                                    <Text variant="Header4">{makeComplianceRequirementHumanReadable(analysis.regulation, analysis.identifier)}</Text>
                                    {/* Edge case: an AI assessment that includes Requirement XYZ has been generated, but now XYZ is not mapped to the control. To avoid more convoluted saving/fetching of data, we have decided simply to omit the tooltip in this case. */}
                                    {requirement && requirement.text && (
                                        <div className={styles.tooltipForComplianceRequirement}>
                                            <FormFieldTooltip text={ellipt(requirement.text, MAX_CHARS_FOR_TOOLTIP)} />
                                        </div>
                                    )}
                                </div>
                                <Text variant="Header5" color="darkGray" noStyles>
                                    Suggested status
                                </Text>
                                <div className={styles.iconAndStatusContainer}>
                                    <FontAwesomeIcon className={analysis.status === GenericStatus.MET ? styles.green : styles.red} icon={analysis.status === GenericStatus.MET ? ICON_POSITIVE : ICON_NEGATIVE} />
                                    <Text noStyles>{analysis.status}</Text>
                                </div>
                                <Text variant="Header5" color="darkGray" noStyles>
                                    Explanation
                                </Text>
                                <Text>{analysis.explanation}</Text>
                                {analysis.assumptions && (
                                    <>
                                        <Text variant="Header5" color="darkGray" noStyles>
                                            Assumptions
                                        </Text>
                                        <Text>{analysis.assumptions}</Text>
                                    </>
                                )}
                                {analysis.suggestions.length > 0 && (
                                    <>
                                        <Text variant="Header5" color="darkGray" noStyles>
                                            Suggestions
                                        </Text>
                                        <ul className={styles.list}>
                                            {analysis.suggestions.map((suggestion, index) => (
                                                <li key={index}>
                                                    <Text variant="PlainText">{suggestion}</Text>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </>
            )}
        </>
    );
};
